// The screens. One component per phase, each with mount() and update().
//
// WHY mount/update AND NOT A RE-RENDER: this is a text-entry game on a phone.
// Replacing the DOM on every state change would blow away the focused input,
// the soft keyboard and whatever the player was halfway through typing — and
// state changes during a round every second, because the countdown ticks. So a
// screen builds its DOM once and only writes the volatile parts afterwards.
//
// That is also the reason the whole renderer is DOM rather than Canvas: four
// text inputs, a letter, a timer and a results table. Canvas would mean
// fighting the Android keyboard for focus, IME composition and viewport
// resize, and reimplementing text selection for nothing.

import type { MatchController, MatchViewState, Phase } from '../state/match';
import { avatarColor, button, el, initials, secondsUntil, setText } from './dom';

export interface Screen {
  readonly root: HTMLElement;
  update(state: MatchViewState, now: number): void;
  /** Called before the screen is replaced. */
  dispose?(): void;
}

export interface ScreenDeps {
  readonly controller: MatchController;
  readonly userId: () => string;
  readonly onExit: () => void;
  readonly onOpenFullGame: () => void;
}

/** Builds the screen for a phase. */
export function screenFor(phase: Phase, deps: ScreenDeps): Screen {
  switch (phase) {
    case 'connecting':
      return statusScreen('Joining a game…', true);
    case 'lobby':
      return new LobbyScreen(deps);
    case 'selectingLetter':
      return new LetterSelectScreen(deps);
    case 'waitingForLetter':
      return new WaitingForLetterScreen();
    case 'round':
      return new RoundScreen(deps);
    case 'scoring':
      return statusScreen('Scoring answers…', true);
    case 'results':
      return new ResultsScreen(deps);
    case 'ended':
      return new GameOverScreen(deps);
    case 'rejected':
      return messageScreen(
        'That game is full',
        'It filled up or already started. Close and try again for a new one.',
        deps,
      );
    case 'error':
      return new ErrorScreen(deps);
  }
}

// --- simple screens ---------------------------------------------------------

function statusScreen(label: string, spinner: boolean): Screen {
  const text = el('p', { class: 'muted' }, [label]);
  const root = el('div', { class: 'center' }, [
    ...(spinner ? [el('div', { class: 'spinner', role: 'status' })] : []),
    text,
  ]);
  return { root, update: () => {} };
}

function messageScreen(title: string, body: string, deps: ScreenDeps): Screen {
  const root = el('div', { class: 'center' }, [
    el('p', { class: 'title' }, [title]),
    el('p', { class: 'muted' }, [body]),
    el('div', { class: 'actions' }, [button('Close', deps.onExit, 'secondary')]),
  ]);
  return { root, update: () => {} };
}

class ErrorScreen implements Screen {
  readonly root: HTMLElement;
  private body = el('p', { class: 'muted' }, ['']);

  constructor(deps: ScreenDeps) {
    this.root = el('div', { class: 'center' }, [
      el('p', { class: 'title error' }, ['Game interrupted']),
      this.body,
      el('div', { class: 'actions' }, [button('Close', deps.onExit, 'secondary')]),
    ]);
  }

  update(state: MatchViewState): void {
    setText(
      this.body,
      state.error === 'kicked'
        ? "You were away too long and lost your place. Close and start a new game."
        : "We lost the connection to the game. Close and start a new game.",
    );
  }
}

class WaitingForLetterScreen implements Screen {
  readonly root: HTMLElement;
  private who = el('p', { class: 'title' }, ['']);

  constructor() {
    this.root = el('div', { class: 'center' }, [
      this.who,
      el('p', { class: 'muted' }, ['Picking the letter for this round.']),
      el('div', { class: 'spinner', role: 'status' }),
    ]);
  }

  update(state: MatchViewState): void {
    setText(this.who, state.pickerUsername ? `${state.pickerUsername} is choosing` : 'Choosing a letter');
  }
}

// --- lobby ------------------------------------------------------------------

class LobbyScreen implements Screen {
  readonly root: HTMLElement;
  private rosterNode = el('div', { class: 'roster' });
  private hint = el('p', { class: 'muted' }, ['']);
  private startButton: HTMLButtonElement;
  private categoriesNode = el('p', { class: 'muted' }, ['']);

  constructor(private readonly deps: ScreenDeps) {
    this.startButton = button('Start game', () => deps.controller.startGame());
    this.startButton.hidden = true;

    this.root = el('div', { class: 'center' }, [
      el('p', { class: 'title' }, ['Waiting for players']),
      this.categoriesNode,
      this.rosterNode,
      this.hint,
      el('div', { class: 'actions' }, [
        this.startButton,
        button('Leave', deps.onExit, 'secondary'),
      ]),
    ]);
  }

  update(state: MatchViewState): void {
    setText(this.categoriesNode, state.categories.join('  ·  '));
    renderRoster(this.rosterNode, state);

    const count = Object.keys(state.roster).length;
    const isHost = state.hostUserId === this.deps.userId();

    // The host sees the button; everyone else is told who they are waiting for,
    // rather than being shown a control that would do nothing.
    this.startButton.hidden = !(isHost && state.canStart);
    setText(
      this.hint,
      state.canStart
        ? isHost
          ? `${count} players ready.`
          : 'Waiting for the host to start.'
        : 'Waiting for one more player…',
    );
  }
}

function renderRoster(node: HTMLElement, state: MatchViewState): void {
  const players = Object.values(state.roster);
  const signature = players.map((p) => p.userId).join(',') + '|' + state.offline.join(',');
  if (node.dataset['sig'] === signature) return; // avoid rebuilding on a tick
  node.dataset['sig'] = signature;

  node.replaceChildren(
    ...players.map((p) => {
      const dim = state.offline.includes(p.userId);
      return el('div', { class: dim ? 'player offline' : 'player' }, [
        el(
          'span',
          { class: 'avatar', style: `background:${avatarColor(p.avatar)}` },
          [initials(p.username)],
        ),
        el('span', { class: 'player-name' }, [p.username || 'Player']),
        ...(dim ? [el('span', { class: 'spinner tiny', role: 'status' })] : []),
      ]);
    }),
  );
}

// --- letter select ----------------------------------------------------------

class LetterSelectScreen implements Screen {
  readonly root: HTMLElement;
  private grid = el('div', { class: 'letters' });
  private built = false;

  constructor(private readonly deps: ScreenDeps) {
    this.root = el('div', { class: 'center' }, [
      el('p', { class: 'title' }, ['Pick a letter']),
      el('p', { class: 'muted' }, ['Everyone answers with this letter.']),
      this.grid,
    ]);
  }

  update(state: MatchViewState): void {
    if (this.built) return;
    this.built = true;
    this.grid.replaceChildren(
      ...state.letterOptions.map((letter) => {
        const used = state.usedLetters.includes(letter);
        const b = button(letter, () => this.deps.controller.selectLetter(letter), 'letter');
        // Used letters stay VISIBLE but disabled. Removing them would reflow
        // the grid mid-match and move the letter under the player's thumb.
        b.disabled = used;
        if (used) b.classList.add('used');
        return b;
      }),
    );
  }
}

// --- round ------------------------------------------------------------------

class RoundScreen implements Screen {
  readonly root: HTMLElement;
  private letterNode = el('div', { class: 'letter-badge' }, ['']);
  private roundNode = el('span', { class: 'muted' }, ['']);
  private timerNode = el('span', { class: 'timer' }, ['']);
  private fields = new Map<string, HTMLInputElement>();
  private form = el('div', { class: 'fields' });
  private submitButton: HTMLButtonElement;
  private built = false;
  /** Set once the deadline passes, so auto-submit fires exactly once. */
  private autoSubmitted = false;

  constructor(private readonly deps: ScreenDeps) {
    this.submitButton = button('Submit', () => deps.controller.submitAnswers('manual'));
    this.root = el('div', { class: 'round' }, [
      el('div', { class: 'round-head' }, [this.roundNode, this.timerNode]),
      this.letterNode,
      this.form,
      el('div', { class: 'actions' }, [this.submitButton]),
    ]);
  }

  update(state: MatchViewState, now: number): void {
    setText(this.letterNode, state.alphabet ?? '');
    setText(this.roundNode, `Round ${state.currentRound} of ${state.totalRounds}`);

    if (!this.built) {
      this.built = true;
      this.buildFields(state);
    }

    const remaining = secondsUntil(state.answersDeadline, now);
    setText(this.timerNode, remaining === null ? '' : `${remaining}s`);
    this.timerNode.classList.toggle('urgent', remaining !== null && remaining <= 5);

    // The DEADLINE is the authority, not a local timer. When it passes we
    // submit whatever is typed — the server is about to score this round with
    // or without us, and a partial answer scores better than none.
    if (remaining === 0 && !this.autoSubmitted && !state.submitted) {
      this.autoSubmitted = true;
      this.deps.controller.submitAnswers('deadline');
    }

    this.submitButton.disabled = state.submitted;
    setText(this.submitButton, state.submitted ? 'Submitted' : 'Submit');
    for (const input of this.fields.values()) input.disabled = state.submitted;
  }

  /**
   * Builds the inputs ONCE per round.
   *
   * Nothing here is rebuilt on update, which is the whole point: an input
   * recreated while the player is typing loses the caret, the composition
   * buffer and the keyboard.
   */
  private buildFields(state: MatchViewState): void {
    this.form.replaceChildren(
      ...state.categories.map((category, index) => {
        const input = el('input', {
          type: 'text',
          class: 'answer',
          placeholder: state.alphabet ? `${category} with ${state.alphabet}` : category,
          autocomplete: 'off',
          autocapitalize: 'words',
          spellcheck: 'false',
          // Every field but the last advances; the last one submits. Matches
          // what the on-screen keyboard's action key implies.
          enterkeyhint: index === state.categories.length - 1 ? 'send' : 'next',
          'aria-label': category,
        });
        input.value = state.answers[category] ?? '';
        input.addEventListener('input', () => {
          this.deps.controller.setAnswer(category, input.value);
        });
        input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          const next = this.fields.get(state.categories[index + 1] ?? '');
          if (next) next.focus();
          else this.deps.controller.submitAnswers('manual');
        });
        this.fields.set(category, input);
        return el('label', { class: 'field' }, [
          el('span', { class: 'field-label' }, [category]),
          input,
        ]);
      }),
    );
  }
}

// --- results ----------------------------------------------------------------

class ResultsScreen implements Screen {
  readonly root: HTMLElement;
  private countdown = el('p', { class: 'muted' }, ['']);
  private table = el('div', { class: 'results' });
  private skipButton: HTMLButtonElement;
  private renderedRound = -1;

  constructor(private readonly deps: ScreenDeps) {
    this.skipButton = button('Next round', () => deps.controller.skipResults(), 'secondary');
    this.skipButton.hidden = true;
    this.root = el('div', { class: 'results-screen' }, [
      el('p', { class: 'title' }, ['Round results']),
      this.table,
      this.countdown,
      el('div', { class: 'actions' }, [this.skipButton]),
    ]);
  }

  update(state: MatchViewState, now: number): void {
    if (state.results && this.renderedRound !== state.currentRound) {
      this.renderedRound = state.currentRound;
      this.buildTable(state);
    }

    const remaining = secondsUntil(state.nextRoundDeadline, now);
    setText(
      this.countdown,
      remaining === null
        ? ''
        : state.isFinalRound
          ? `Final results in ${remaining}s`
          : `Next round in ${remaining}s`,
    );

    this.skipButton.hidden = !(state.hostUserId === this.deps.userId()) || state.isFinalRound;
  }

  private buildTable(state: MatchViewState): void {
    const results = state.results;
    if (!results) return;

    this.table.replaceChildren(
      ...results.ranking.map((entry) => {
        // Falls back to everyone who has EVER been in this match, not the live
        // roster: a player who quit or timed out keeps their accumulated score
        // and still appears here. Reading only the live roster renders them as
        // "Unknown Player" — the Flutter client hit exactly this.
        const player = state.everSeen[entry.userId];
        const name = player?.username || 'Unknown Player';
        const mine = entry.userId === this.deps.userId();
        const answers = results.results[entry.userId] ?? {};

        return el('div', { class: mine ? 'result-row mine' : 'result-row' }, [
          el('div', { class: 'result-head' }, [
            el('span', { class: 'rank' }, [`#${entry.ranking}`]),
            el('span', { class: 'player-name' }, [name]),
            el('span', { class: 'score' }, [`${entry.score}`]),
          ]),
          el(
            'div',
            { class: 'result-answers' },
            state.categories.map((category) => {
              const pair = answers[category];
              return el('div', { class: 'result-answer' }, [
                el('span', { class: 'muted' }, [category]),
                el('span', {}, [pair?.answer || '—']),
                el('span', { class: pair && pair.score > 0 ? 'good' : 'zero' }, [
                  `+${pair?.score ?? 0}`,
                ]),
              ]);
            }),
          ),
        ]);
      }),
    );
  }
}

// --- game over --------------------------------------------------------------

class GameOverScreen implements Screen {
  readonly root: HTMLElement;
  private headline = el('p', { class: 'title' }, ['']);
  private detail = el('p', { class: 'muted' }, ['']);

  constructor(deps: ScreenDeps) {
    this.root = el('div', { class: 'center' }, [
      this.headline,
      this.detail,
      el('div', { class: 'actions' }, [
        button('Done', deps.onExit),
        button('Get the full game', deps.onOpenFullGame, 'secondary'),
      ]),
    ]);
  }

  update(state: MatchViewState): void {
    const rank = state.reward?.rank ?? 0;
    // rank === 0 is NO SIGNAL — an empty match-end payload from a legacy
    // server, or the idempotent replay once rewards were already granted.
    // Showing "you came last" for it would be wrong for a player who won.
    setText(this.headline, rank === 1 ? 'You won' : rank > 1 ? `You finished #${rank}` : 'Match complete');
    setText(this.detail, `${state.totalScore} points. Your score has been sent to the leaderboard.`);
  }
}
